"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/40 rounded-md bg-card">
        <p className="font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
          Enviar otra solicitud
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Nombre completo
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Ej. Juan Pérez" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Correo electrónico
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="juan@empresa.com" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Teléfono de contacto
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+52 (55) 0000-0000" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="event_type" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Servicio de interés
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-transparent border-0 border-b border-border px-0 h-11 w-full text-base font-default text-foreground data-[placeholder]:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary [&_svg]:text-primary rounded-md">
                  <SelectValue placeholder="Seleccione una opción" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border rounded-md">
                <SelectItem value="Seguridad Privada">Seguridad Privada</SelectItem>
                <SelectItem value="Monitoreo C4">Monitoreo C4 / CCTV</SelectItem>
                <SelectItem value="Rastreo GPS">Rastreo GPS Vehicular</SelectItem>
                <SelectItem value="Protección Ejecutiva">Protección Ejecutiva</SelectItem>
                <SelectItem value="Consultoría">Consultoría de Riesgos</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="event_date" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Fecha estimada de inicio
              </FormLabel>
              <FormControl>
                <Input {...field} type="date" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary [color-scheme:dark] rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Número de elementos requeridos
              </FormLabel>
              <FormControl>
                <Input {...field} type="number" min={1} max={200} placeholder="e.g., 5" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Detalles del requerimiento
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describa las necesidades específicas de seguridad para su empresa o residencia..." className="bg-transparent border-0 border-b border-border px-0 py-2 text-base font-default text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary resize-none transition-all focus:min-h-32 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="updates" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground rounded-md" />
            </FormControl>
            <FormLabel className="text-sm font-default text-foreground/80 leading-relaxed cursor-pointer">
              Acepto recibir información sobre protocolos de seguridad y actualizaciones tecnológicas.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} variant="outline" className="group w-full h-14 bg-primary text-primary-foreground border border-primary hover:bg-foreground hover:text-background font-mono text-xs uppercase tracking-[0.25em] rounded-md transition-all">
        <span>{isSubmitting ? "Enviando solicitud..." : "Solicitar Cotización"}</span>
        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Button>
    </Form>;
}
export default function FormularioDeCotizaciN() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3479" id="formulario-de-cotización" className="bg-background text-foreground py-24 md:py-[180px] px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-16 lg:gap-24">
          <div ref={leftRef} className={`self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-10">
              § 06 / CONTACTO
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[64px] font-bold leading-[1.05] text-foreground mb-8">
              <span className="font-light">Protegemos</span>
              <br />
              <em className="italic font-light text-primary">su patrimonio.</em>
            </h2>
            <p className="font-default text-[17px] leading-relaxed text-muted-foreground mb-16 max-w-md">
              Nuestros consultores de seguridad analizan cada caso con rigor técnico. Reciba una propuesta personalizada diseñada para mitigar riesgos específicos.
            </p>

            <div className="border-t border-border/40 pt-10 space-y-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Atención inmediata 24/7
              </p>
              <div className="space-y-2">
                <Link to="tel:+525500000000" className="block font-default text-lg font-semibold text-primary hover:text-primary/80 transition-colors">+52 55 1867 1526</Link>
                <Link to="mailto:contacto@grupoposeidon.com.mx" className="block font-mono text-sm text-primary hover:text-primary/80 transition-colors">
                  contacto@grupoposeidon.com.mx
                </Link>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">
              * Toda la información es tratada bajo estrictos protocolos de confidencialidad.
            </p>

            <div className="bg-card border border-primary/30 rounded-md p-8 md:p-16 shadow-xl relative">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/50" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/50" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/50" />
              
              <FormProvider formKey="cotizacion_form_contact" formId="cotizacion_form_contact" sectionName="formulario-de-cotización" defaultValues={{
              name: "",
              email: "",
              phone: "",
              event_date: "",
              guests: "",
              event_type: "",
              message: "",
              updates: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              event_date: {
                required: true
              },
              guests: {
                required: true,
                min: 1,
                max: 200
              },
              event_type: {
                required: true
              }
            }} submitText="Solicitar Cotización" successMessage="Su solicitud ha sido recibida. Un consultor de seguridad se pondrá en contacto con usted en menos de 24 horas." errorMessage="No pudimos enviar su solicitud. Por favor, intente de nuevo o contáctenos directamente.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}